import { Input, InputNumber, Select } from 'antd';
import React, { useEffect } from 'react';
import { LabeledField } from '../../../../components/form-fields/labeled-field';
import { Section } from '../../../../components/form-fields/section';
import { BusinessUnitsSelectTree } from '../../../../components/region-selectors/business-units-select-tree';
import { useHandleFormSelectionChange } from '../../../../components/region-selectors/business-units-select-tree/hooks/use-handle-form-selection-change';
import { useApplicationSelectionStore } from '../../../../contexts/mobx';
import InteracIcon from '../../../../icons/Interac.svg?react';
import type { PromotionForm } from '../../../../stores/promotion';
import { ApplicationSelection } from '../../../../types/app-selection';
import style from './style.module.scss';

interface Category {
    id: number;
    requireAgeVerification: boolean;
    items: {
        title: string;
        language: string;
    }[];
}

interface Offer {
    id: number;
    items: {
        title?: string;
        language: string;
    }[];
}

// Sorted by the title the option shows, numbers in numeric order ("Offer 2" before "Offer 10").
function byTitle(
    a: { items: { title?: string }[] },
    b: { items: { title?: string }[] }
) {
    return (a.items[0]?.title ?? '').localeCompare(
        b.items[0]?.title ?? '',
        undefined,
        { numeric: true, sensitivity: 'base' }
    );
}

// antd's default search only ignores case, so "epicerie" would miss "Épicerie": fold accents too.
function foldAccents(text: string) {
    return text
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '')
        .toLowerCase();
}

function matchesTitle(input: string, option?: { title?: string }) {
    return foldAccents(option?.title ?? '').includes(foldAccents(input));
}

interface ConfigSectionProps {
    values: PromotionForm;
    setFieldValue: (field: string, value: unknown) => void;
    setCategory: (categoryId: number) => void;
    categoryStore: {
        isFetchingActiveCategories: boolean;
        activeCategories?: { results: Category[] } | null;
    };
    offerStore: {
        offers: { results: Offer[] } | null;
        isFetchingOffers: boolean;
        getOffersById: (id: number) => Offer | undefined;
        fetchOffers: () => Promise<void>;
    };
    handleChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

export const ConfigSection = ({
    values,
    setFieldValue,
    handleChange,
    categoryStore,
    offerStore,
    setCategory,
}: ConfigSectionProps) => {
    const { handleProvinceSelectionChange } = useHandleFormSelectionChange();
    const { selectedApplication } = useApplicationSelectionStore();
    const isCoucheTard =
        selectedApplication === ApplicationSelection.COUCHE_TARD;

    useEffect(() => {
        offerStore.fetchOffers();
    }, [offerStore]);

    return (
        <Section title="Config">
            <div className={style.config_fields_container}>
                <LabeledField label="Category">
                    <Select
                        loading={categoryStore.isFetchingActiveCategories}
                        onChange={setCategory}
                        value={values.category?.id}
                        optionLabelProp="title"
                        showSearch
                        filterOption={matchesTitle}
                    >
                        {categoryStore.activeCategories?.results
                            .filter(
                                (category) =>
                                    !isCoucheTard ||
                                    !category.requireAgeVerification
                            )
                            .sort(byTitle)
                            .map((category) => (
                                <Select.Option
                                    value={category.id}
                                    key={category.id}
                                    title={category.items[0].title}
                                >
                                    <span className={style.category_option}>
                                        {category.items[0].title}
                                        {category.requireAgeVerification && (
                                            <InteracIcon
                                                className={
                                                    style.age_verification_icon
                                                }
                                                role="img"
                                                aria-label="Age verification required"
                                            />
                                        )}
                                    </span>
                                </Select.Option>
                            ))}
                    </Select>
                </LabeledField>
                <LabeledField label="Promotional Offer">
                    <Select
                        disabled={
                            !offerStore.offers || offerStore.isFetchingOffers
                        }
                        loading={offerStore.isFetchingOffers}
                        onChange={(id) => {
                            const selectedOffer = offerStore.getOffersById(
                                Number(id)
                            );
                            if (selectedOffer) {
                                setFieldValue(
                                    'promotionalOffer',
                                    selectedOffer
                                );
                                setFieldValue('promotionalOfferId', id);
                            } else {
                                setFieldValue('promotionalOffer', null);
                                setFieldValue('promotionalOfferId', null);
                            }
                        }}
                        allowClear
                        value={values.promotionalOffer?.id}
                        showSearch
                        filterOption={matchesTitle}
                    >
                        {offerStore.offers?.results
                            .slice()
                            .sort(byTitle)
                            .map((offer) => (
                                <Select.Option
                                    value={offer.id}
                                    key={offer.id}
                                    title={offer.items[0].title}
                                >
                                    {offer.items[0].title}
                                </Select.Option>
                            ))}
                    </Select>
                </LabeledField>
                <LabeledField label="Region">
                    <BusinessUnitsSelectTree
                        value={values.provinces?.map((p) => p.provinceId) || []}
                        onChange={(selectedProvinceIds) => {
                            const updatedProvinces = handleProvinceSelectionChange(
                                selectedProvinceIds,
                                values.provinces || []
                            );
                            setFieldValue('provinces', updatedProvinces);
                        }}
                    />
                </LabeledField>
                <input type="hidden" name="type" value={values.type} />
                <LabeledField label="Discount Value">
                    <InputNumber
                        value={values.discountValue ?? 0}
                        name="discountValue"
                        onChange={(value) =>
                            setFieldValue('discountValue', value)
                        }
                    />
                </LabeledField>
                {values.type === 'coupon' && (
                    <>
                        <LabeledField label="Quantity">
                            <InputNumber
                                min={0}
                                value={values.quantity ?? 0}
                                name="quantity"
                                onChange={(value) =>
                                    setFieldValue('quantity', value)
                                }
                            />
                        </LabeledField>
                        <LabeledField label="Activation Code (UPC-A, 12 digits)">
                            <Input
                                value={values.activationCode ?? ''}
                                name="activationCode"
                                inputMode="numeric"
                                onChange={(e) => {
                                    const digits = e.target.value
                                        .replace(/\D/g, '')
                                        .slice(0, 12);
                                    setFieldValue('activationCode', digits);
                                }}
                                suffix={
                                    <span>
                                        {(values.activationCode ?? '').length}
                                        /12
                                    </span>
                                }
                            />
                        </LabeledField>
                    </>
                )}
            </div>
        </Section>
    );
};
